// HTML for preschool invoice and receipt PDFs, and the email bodies that go with them.
//
// The invoice follows the Google Docs templates the preschool invoice generator used ("BHP Invoice Template"
// 13TQeM2Q1dNMPiZyBD7Sv7sAjXYclI0Xjcu3JFUgnG4Q and the registration one 1aonCmNyOF4WutkRpI1tFliv4iuVAG8_c4itzuDLkDj8),
// in the Holiday Club documents' styling.

import { BEE_LOGO_DATA_URI } from "../holiday/beeLogo";
import { COMPANY, STYLE, escapeHtml, formatDate } from "../holiday/holidayDocuments";
import { formatPounds, type PreschoolInvoice } from "./preschoolInvoicing";

const document = (params: {
  title: string;
  invoice: PreschoolInvoice;
  meta: [string, string][];
  thanks: string;
  totalLabel: string;
  footer: string;
}) => {
  const { invoice } = params;
  const rows = invoice.items.map((item) => `
      <tr><td>${escapeHtml(item.description)}</td><td>${escapeHtml(item.quantity)}</td>
        <td>£${formatPounds(item.unitPricePence)}</td><td>£${formatPounds(item.totalPence)}</td></tr>`).join("");
  return `<!doctype html>
<html lang="en">
<head><meta charset="utf-8"><title>${escapeHtml(params.title)}</title><style>${STYLE}
  .child { margin-top: 40px; } .child b { color: #000; }
  table.lines { margin-top: 12px; }
</style></head>
<body>
  <div class="top">
    <div class="company">
      <h1>${COMPANY.name}</h1>
      <div>${COMPANY.address}</div>
      <div><a href="${COMPANY.website}">${COMPANY.website}</a></div>
      <div>${COMPANY.email}</div>
      <div>${COMPANY.phone}</div>
    </div>
    <div class="title"><h2>${escapeHtml(params.title)}</h2><img src="${BEE_LOGO_DATA_URI}" alt=""></div>
  </div>
  <div class="parties">
    <div class="customer">
      <div>${escapeHtml(invoice.parentName)}</div>
      <div>${escapeHtml(invoice.address).replace(/\n/g, "<br>")}</div>
    </div>
    <table class="meta">
      ${params.meta.map(([label, value]) => `<tr><td>${label}</td><td>${escapeHtml(value)}</td></tr>`).join("")}
    </table>
  </div>
  <div class="child">Childcare Services for: <b>${escapeHtml(invoice.childName)}</b></div>
  <table class="lines">
    <thead><tr><th style="width:45%">DESCRIPTION</th><th style="width:17%">QTY</th><th>UNIT PRICE</th><th>TOTAL</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="summary">
    <div class="thanks">${params.thanks}</div>
    <table class="totals">
      <tr><td>SUBTOTAL</td><td>£${formatPounds(invoice.subtotalPence)}</td></tr>
      <tr><td>DISCOUNT</td><td>£${formatPounds(invoice.discountPence)}</td></tr>
      <tr class="due"><td>${params.totalLabel}</td><td>£${formatPounds(invoice.totalPence)}</td></tr>
    </table>
  </div>
  <div class="terms">${params.footer}</div>
</body>
</html>`;
};

const termsLink = `<p>Terms &amp; Conditions: <a href="${COMPANY.terms}">${COMPANY.terms}</a></p>`;

export const renderPreschoolInvoiceHtml = (invoice: PreschoolInvoice) =>
  document({
    title: "INVOICE",
    invoice,
    meta: [
      ["Invoice No:", `#${invoice.ref}`],
      ["Invoice Date:", formatDate(invoice.issuedDate)],
      ["Invoice Period:", invoice.period],
      ["Due Date:", formatDate(invoice.dueDate)],
    ],
    thanks: "Thank you for your business!",
    totalLabel: "Balance Due",
    footer: `
      <h3>Instructions</h3>
      <p>Account Holder Name: ${COMPANY.accountHolder}</p>
      <p>Account Number: ${COMPANY.accountNumber}</p>
      <p>Sort Code: ${COMPANY.sortCode}</p>
      <p>&nbsp;</p>
      <p>Use invoice number as the reference.</p>
      <p>&nbsp;</p>
      ${termsLink}`,
  });

export const renderPreschoolReceiptHtml = (invoice: PreschoolInvoice, paymentDate: string, receiptDate: string) =>
  document({
    title: "Receipt",
    invoice,
    meta: [
      ["Receipt No:", `#R${invoice.ref}`],
      ["Invoice Period:", invoice.period],
      ["Payment Date:", formatDate(paymentDate)],
      ["Receipt Date:", formatDate(receiptDate)],
    ],
    thanks: "Thank you for your payment\nIf you have any questions about this receipt, please contact us.",
    totalLabel: "Amount Paid",
    footer: `<p><b>Payment method:</b> Bank Transfer</p><p>&nbsp;</p>${termsLink}`,
  });

const signOff = `<br>
If you have any questions, feel free to reach out. We are always here to help.<br>
<br>
<br>
Best,<br>
Olesia P.<br>
Bee Happy Preschool`;

export const preschoolInvoiceSubject = (invoice: PreschoolInvoice) => `Bee Happy Preschool invoice ${invoice.ref} - ${invoice.period}`;
export const preschoolReceiptSubject = (invoice: PreschoolInvoice) => `Bee Happy Preschool receipt ${invoice.ref}`;

export const renderPreschoolInvoiceEmail = (invoice: PreschoolInvoice) => `Hi ${escapeHtml(invoice.parentName)},<br>
<br>
Please find attached the invoice for ${escapeHtml(invoice.childName)} for ${escapeHtml(invoice.period)}: £${formatPounds(invoice.totalPence)},
due by ${formatDate(invoice.dueDate)}.<br>
<br>
Please pay by bank transfer and use the invoice number <b>${invoice.ref}</b> as the reference.<br>${signOff}`;

export const renderPreschoolReceiptEmail = (invoice: PreschoolInvoice) => `Hi ${escapeHtml(invoice.parentName)},<br>
<br>
Thank you, we have received your payment for invoice ${invoice.ref} (${escapeHtml(invoice.childName)}, ${escapeHtml(invoice.period)}).<br>
<br>
You will find your receipt attached to this email.<br>${signOff}`;

export const preschoolInvoiceFileName = (invoice: PreschoolInvoice) => `invoice#${invoice.ref} ${invoice.parentName}.pdf`;
export const preschoolReceiptFileName = (invoice: PreschoolInvoice) => `receipt#R${invoice.ref} ${invoice.parentName}.pdf`;
